import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/components/ui/Card";
import { Alert, AlertDescription } from "src/components/ui/Alert";

const PrivacyPage = () => {
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header Section */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-lg text-gray-600">Our Commitment to Privacy</p>
      </div>

      {/* Introduction Card */}
      <Card>
        <CardContent className="pt-6">
          <p className="text-gray-700">
            At WeKruit, we are committed to protecting your privacy and handling
            your data with the utmost responsibility and transparency. This
            privacy policy outlines how we collect, use, protect, and manage
            your information when you use our AI-powered mock interview
            platform.
          </p>
        </CardContent>
      </Card>

      {/* Data Collection Section */}
      <Card>
        <CardHeader>
          <CardTitle>Data Collection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">Personal Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Name and email address</li>
              <li>Professional background</li>
              <li>Account credentials</li>
              <li>Contact information you choose to provide</li>
            </ul>
            <p className="text-gray-700 mt-2">
              <strong>Note:</strong> We do not collect sensitive data such as
              financial information, health records, or government-issued IDs.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">Usage Data</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Platform interaction data</li>
              <li>Feature usage patterns</li>
              <li>System performance metrics</li>
              <li>Error logs and debugging information</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">
              Professional Development Data
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>User-uploaded resumes</li>
              <li>Career preferences</li>
              <li>Industry-specific selections</li>
              <li>Skills and qualifications</li>
              <li>Company preferences and target roles</li>
            </ul>
          </div>

          <Alert>
            <AlertDescription>
              Important: WeKruit does not collect, store, or process any facial
              or audio data from users.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Data Usage Section */}
      <Card>
        <CardHeader>
          <CardTitle>How We Use Your Data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">Service Delivery</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Generating personalized mock interview questions</li>
              <li>Creating customized interview scenarios</li>
              <li>Providing tailored feedback and performance analysis</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">Platform Improvement</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Enhancing feature functionality</li>
              <li>Optimizing user experience</li>
              <li>Developing new capabilities</li>
              <li>Analyzing platform performance</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">
              Support and Communication
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Responding to your inquiries</li>
              <li>Sending service-related notifications</li>
              <li>Providing technical support</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Data Sharing Section */}
      <Card>
        <CardHeader>
          <CardTitle>Data Sharing and Disclosure</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4">
            We maintain strict controls over your data and do not sell it to
            third parties. Data sharing is limited to:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Third-party service providers necessary for platform operation
              (e.g., hosting, analytics)
            </li>
            <li>Partners bound by strict confidentiality agreements</li>
            <li>Legal compliance requirements</li>
            <li>With your explicit consent</li>
          </ul>
          <p className="mt-4">
            All service providers are carefully vetted and contractually
            obligated to protect your data.
          </p>
        </CardContent>
      </Card>

      {/* Data Security & Protection Section */}
      <Card>
        <CardHeader>
          <CardTitle>Data Security &amp; Protection</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700">
            We take the security of your data very seriously. Our platform
            employs industry-standard security measures to ensure your
            information remains safe:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>
              <strong>Encryption:</strong> All data transmitted to and from our
              platform is encrypted via HTTPS. User data, including passwords,
              is stored using robust encryption methods and cryptographic
              hashing techniques.
            </li>
            <li>
              <strong>Access Controls:</strong> Access to your data is limited
              to authorized personnel who require it for platform operation and
              support.
            </li>
            <li>
              <strong>Firebase Security:</strong> We exclusively use Google
              Firebase for data storage and processing. Firebase provides strong
              encryption both at rest and in transit, ensuring your data is
              securely handled.
            </li>
            <li>
              <strong>Data Deletion:</strong> You have the right to request
              deletion of your data at any time. Once a deletion request is
              made, your personal information will be promptly and securely
              removed from our systems.
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Your Rights Section */}
      <Card>
        <CardHeader>
          <CardTitle>Your Rights</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc pl-6 space-y-2">
            <li>Access: Request a copy of your personal data</li>
            <li>Correction: Update or correct inaccurate information</li>
            <li>Deletion: Request removal of your data from our systems</li>
            <li>
              Portability: Receive your data in a structured, common format
            </li>
            <li>
              Consent Withdrawal: Opt-out of specific data processing activities
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Contact Information Section */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-2">
            For privacy-related concerns or requests, contact us at:
          </p>
          <p className="font-medium">
            Email:{" "}
            <a
              href="mailto:admin1@wekruit.com"
              className="text-blue-600 hover:underline"
            >
              admin1@wekruit.com
            </a>
          </p>
          <p className="font-medium">
            Web:{" "}
            <a
              href="https://www.wekruit.com/privacy"
              className="text-blue-600 hover:underline"
            >
              www.wekruit.com/privacy
            </a>
          </p>
        </CardContent>
      </Card>

      {/* Legal Compliance Section */}
      <Card>
        <CardHeader>
          <CardTitle>Legal Compliance</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4">
            This policy complies with applicable data protection regulations,
            including:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>General Data Protection Regulation (GDPR)</li>
            <li>California Consumer Privacy Act (CCPA)</li>
            <li>Other relevant privacy laws and regulations</li>
          </ul>
          <p className="mt-4">
            We regularly review and update our policies to maintain compliance
            with evolving privacy regulations.
          </p>
        </CardContent>
      </Card>

      {/* Google Workspace API Data Usage Section */}
      <Card>
        <CardHeader>
          <CardTitle>Google Workspace API Data Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700">
            We confirm that any data obtained through Google Workspace APIs is
            used solely to support the core functionalities of our platform.
            This data is not used to develop, improve, or train generalized
            artificial intelligence (AI) or machine learning (ML) models.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrivacyPage;
